import Breadcrumbs from '@/components/Breadcrumbs';
import AlertBanner from '@/components/common/AlertBanner';
import EmptyState from '@/components/common/EmptyState';
import FilterChip from '@/components/common/FilterChip';
import ClassCard, { ClassData } from '@/components/course/ClassCard';
import AmaSeriesListItem from '@/components/course/SectionCard/AmaSeriesListItem';
import AssignmentListItem from '@/components/course/SectionCard/AssignmentListItem';
import EvaluationListItem from '@/components/course/SectionCard/EvaluationListItem';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import { useThemeColors } from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { AmaSeries, AssignmentSummary, EvaluationSummary } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import clsx from 'clsx';
import {
    ArrowLeft,
    BookOpen,
    ChevronDown,
    ChevronRight,
    ClipboardList,
    FolderOpen,
    Play,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CourseData {
    id: number;
    title: string;
    slug: string;
}

interface TopicGroup {
    id: number;
    title: string;
    order: number;
    classes: ClassData[];
    assignments: AssignmentSummary[];
    evaluations: EvaluationSummary[];
}

interface SectionData {
    id: number;
    title: string;
    order: number;
    topics?: {
        id: number;
        title: string;
        order?: number;
    }[];
    classes: ClassData[];
    assignments?: AssignmentSummary[];
    evaluations?: EvaluationSummary[];
    ama_series?: AmaSeries[];
}

type PageProps = {
    course: CourseData;
    section: SectionData;
    is_enrolled?: boolean;
};

const SectionDetailContent: React.FC = () => {
    const { t } = useTranslation();
    const { props } = usePage<PageProps>();
    const { course, section, is_enrolled } = props;
    const colors = useThemeColors();
    const { route } = useRoutes();

    // State for topic filter (null = show all)
    const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);

    // Group classes and topic-attached assignments by topic
    const topicGroups = useMemo(() => {
        const groups: Map<number, TopicGroup> = new Map();
        const topicOrderById = new Map<number, number>();
        const sortedTopics = (section.topics || [])
            .slice()
            .sort(
                (a, b) =>
                    (a.order ?? Number.MAX_SAFE_INTEGER) -
                    (b.order ?? Number.MAX_SAFE_INTEGER)
            );

        sortedTopics.forEach((topic, index) => {
            const topicOrder = topic.order ?? index;
            topicOrderById.set(topic.id, topicOrder);
            groups.set(topic.id, {
                id: topic.id,
                title: topic.title,
                order: topicOrder,
                classes: [],
                assignments: [],
                evaluations: [],
            });
        });

        section.classes.forEach((classData) => {
            const topicId = classData.topic.id;
            if (!groups.has(topicId)) {
                groups.set(topicId, {
                    id: topicId,
                    title: classData.topic.title,
                    order:
                        topicOrderById.get(topicId) ?? Number.MAX_SAFE_INTEGER,
                    classes: [],
                    assignments: [],
                    evaluations: [],
                });
            }
            groups.get(topicId)!.classes.push(classData);
        });

        (section.assignments || []).forEach((assignment) => {
            if (!assignment.topic_id) {
                return;
            }
            const topicTitle = assignment.topic_title?.trim();
            if (!groups.has(assignment.topic_id)) {
                groups.set(assignment.topic_id, {
                    id: assignment.topic_id,
                    title: topicTitle || `Topic ${assignment.topic_id}`,
                    order:
                        topicOrderById.get(assignment.topic_id) ??
                        Number.MAX_SAFE_INTEGER,
                    classes: [],
                    assignments: [],
                    evaluations: [],
                });
            }
            const group = groups.get(assignment.topic_id)!;
            if (topicTitle && group.title === `Topic ${assignment.topic_id}`) {
                group.title = topicTitle;
            }
            group.assignments.push(assignment);
        });

        (section.evaluations || []).forEach((evaluation) => {
            if (!evaluation.topic_id) {
                return;
            }
            const topicTitle = evaluation.topic_title?.trim();
            if (!groups.has(evaluation.topic_id)) {
                groups.set(evaluation.topic_id, {
                    id: evaluation.topic_id,
                    title: topicTitle || `Topic ${evaluation.topic_id}`,
                    order:
                        topicOrderById.get(evaluation.topic_id) ??
                        Number.MAX_SAFE_INTEGER,
                    classes: [],
                    assignments: [],
                    evaluations: [],
                });
            }
            const group = groups.get(evaluation.topic_id)!;
            if (topicTitle && group.title === `Topic ${evaluation.topic_id}`) {
                group.title = topicTitle;
            }
            group.evaluations.push(evaluation);
        });

        return Array.from(groups.values())
            .map((group) => ({
                ...group,
                classes: group.classes
                    .slice()
                    .sort((a, b) => a.order - b.order),
            }))
            .sort((a, b) => a.order - b.order)
            .filter(
                (group) =>
                    group.classes.length > 0 ||
                    group.assignments.length > 0 ||
                    group.evaluations.length > 0
            );
    }, [
        section.classes,
        section.assignments,
        section.evaluations,
        section.topics,
    ]);

    // Filter topics based on selection
    const filteredTopicGroups = useMemo(() => {
        if (selectedTopicId === null) {
            return topicGroups;
        }
        return topicGroups.filter((topic) => topic.id === selectedTopicId);
    }, [topicGroups, selectedTopicId]);

    const [expandedTopicIds, setExpandedTopicIds] = useState<Set<number>>(
        new Set()
    );

    useEffect(() => {
        const visibleTopicIds = new Set(
            filteredTopicGroups.map((topic) => topic.id)
        );
        const isSingleTopicSection = topicGroups.length === 1;
        setExpandedTopicIds((previous) => {
            const next = new Set<number>();
            visibleTopicIds.forEach((id) => {
                if (isSingleTopicSection || previous.has(id)) {
                    next.add(id);
                }
            });
            return next;
        });
    }, [filteredTopicGroups, topicGroups.length]);

    const sectionAssignments = useMemo(() => {
        const sectionLevel: AssignmentSummary[] = [];
        (section.assignments || []).forEach((assignment) => {
            if (!assignment.topic_id) {
                sectionLevel.push(assignment);
            }
        });
        return sectionLevel;
    }, [section.assignments]);

    const sectionEvaluations = useMemo(() => {
        const sectionLevel: EvaluationSummary[] = [];
        (section.evaluations || []).forEach((evaluation) => {
            if (!evaluation.topic_id) {
                sectionLevel.push(evaluation);
            }
        });
        return sectionLevel;
    }, [section.evaluations]);

    const amaSeries = section.ama_series || [];

    const breadcrumbItems = [
        {
            label: t('course.sectionDetail.courses'),
            href: route('home').toString(),
        },
        { label: course.title, href: `/courses/${course.slug}` },
        { label: section.title },
    ];

    const handleClassClick = (classData: ClassData) => {
        window.location.href = `/courses/${course.slug}/classes/${classData.id}`;
    };

    const toggleTopic = (topicId: number) => {
        setExpandedTopicIds((previous) => {
            const next = new Set(previous);
            if (next.has(topicId)) {
                next.delete(topicId);
            } else {
                next.add(topicId);
            }
            return next;
        });
    };

    return (
        <AppLayout>
            <PageTitle title={section.title} />
            <div className="min-h-screen">
                <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                    {/* Breadcrumbs */}
                    <div className="mb-6">
                        <Breadcrumbs items={breadcrumbItems} />
                    </div>

                    {!is_enrolled && (
                        <AlertBanner
                            variant="warning"
                            title={t('course.notEnrolledBanner.heading')}
                            className="mb-6"
                        >
                            {t('course.notEnrolledBanner.message')}
                        </AlertBanner>
                    )}

                    {/* Back to Course Link */}
                    <div className="mb-6">
                        <Link
                            href={`/courses/${course.slug}`}
                            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors duration-200 ${colors.border} ${colors.buttonSecondary}`}
                        >
                            <ArrowLeft size={16} />
                            <span className="font-medium">
                                {t('course.backToCourse')}
                            </span>
                        </Link>
                    </div>

                    {/* Section Header */}
                    <GlassCard className="mb-8">
                        <div className="p-6 lg:p-8">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div>
                                    <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-white">
                                        {section.title}
                                    </h1>
                                    <p className="text-gray-600 dark:text-neutral-400">
                                        {t('course.sectionDetail.fromCourse')}{' '}
                                        <span className="font-semibold">
                                            {course.title}
                                        </span>
                                    </p>
                                </div>

                                <div className="flex items-center space-x-4">
                                    <div
                                        className={`flex items-center space-x-2 rounded-full px-4 py-2 ${colors.accent}`}
                                    >
                                        <BookOpen
                                            className={`h-5 w-5 text-${colors.primary}`}
                                        />
                                        <span
                                            className={`font-medium ${colors.accentText}`}
                                        >
                                            {section.classes.length}{' '}
                                            {t('course.classes', {
                                                count: section.classes.length,
                                            })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </GlassCard>

                    {/* Topic Filter */}
                    {topicGroups.length > 1 && (
                        <div className="mb-6">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="mr-2 text-sm font-medium text-gray-700 dark:text-neutral-300">
                                    {t('course.filterByTopic')}:
                                </span>
                                <FilterChip
                                    active={selectedTopicId === null}
                                    onClick={() => {
                                        setSelectedTopicId(null);
                                        setExpandedTopicIds(new Set());
                                    }}
                                >
                                    {t('course.allTopics')}
                                </FilterChip>
                                {topicGroups.map((topic) => (
                                    <FilterChip
                                        key={topic.id}
                                        active={selectedTopicId === topic.id}
                                        onClick={() => {
                                            setSelectedTopicId(topic.id);
                                            setExpandedTopicIds(
                                                new Set([topic.id])
                                            );
                                        }}
                                    >
                                        {topic.title}
                                    </FilterChip>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Classes by Topic */}
                    <div className="space-y-8">
                        {filteredTopicGroups.length === 0 &&
                        sectionAssignments.length === 0 &&
                        sectionEvaluations.length === 0 ? (
                            <EmptyState
                                icon={Play}
                                title={t('course.noClassesAvailable')}
                                description={t(
                                    'course.sectionDetail.thisSection'
                                )}
                            />
                        ) : (
                            filteredTopicGroups.map((topic) => {
                                const hasAssignments =
                                    topic.assignments.length > 0;
                                const isAssignmentOnlyTopic =
                                    hasAssignments &&
                                    topic.classes.length === 0;
                                const isExpanded = expandedTopicIds.has(
                                    topic.id
                                );
                                const headerClass = isAssignmentOnlyTopic
                                    ? isExpanded
                                        ? 'border-amber-300 bg-amber-50/50'
                                        : 'border-amber-200 bg-amber-50/20 hover:border-amber-300 hover:bg-amber-50/40'
                                    : isExpanded
                                      ? 'border-emerald-300 bg-emerald-50/40 dark:bg-emerald-900/20 dark:border-emerald-700'
                                      : 'border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 hover:border-emerald-300 hover:bg-emerald-50/20';
                                const rightPillClass = isAssignmentOnlyTopic
                                    ? 'bg-amber-100 text-amber-800'
                                    : isExpanded
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-neutral-300';
                                const rightIconClass = isAssignmentOnlyTopic
                                    ? 'border-amber-300 bg-white text-amber-700'
                                    : isExpanded
                                      ? 'border-emerald-300 bg-white text-emerald-700'
                                      : 'border-gray-300 dark:border-neutral-600 bg-gray-50 dark:bg-neutral-700 text-gray-600 dark:text-neutral-400';

                                return (
                                    <div key={topic.id} className="space-y-4">
                                        {/* Topic Header */}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                toggleTopic(topic.id)
                                            }
                                            className={clsx(
                                                'flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition-all',
                                                headerClass
                                            )}
                                            aria-expanded={isExpanded}
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div
                                                    className={clsx(
                                                        'flex h-10 w-10 items-center justify-center rounded-lg',
                                                        isAssignmentOnlyTopic
                                                            ? 'bg-amber-100'
                                                            : colors.accent
                                                    )}
                                                >
                                                    {isAssignmentOnlyTopic ? (
                                                        <ClipboardList className="h-5 w-5 text-amber-700" />
                                                    ) : (
                                                        <FolderOpen
                                                            className={`h-5 w-5 text-${colors.primary}`}
                                                        />
                                                    )}
                                                </div>
                                                <div>
                                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                                        {topic.title}
                                                    </h2>
                                                    <p className="text-sm text-gray-600 dark:text-neutral-400">
                                                        {topic.classes.length}{' '}
                                                        {t('course.classes', {
                                                            count: topic.classes
                                                                .length,
                                                        })}
                                                        {topic.assignments
                                                            .length > 0 && (
                                                            <>
                                                                {' '}
                                                                •{' '}
                                                                {
                                                                    topic
                                                                        .assignments
                                                                        .length
                                                                }{' '}
                                                                {t(
                                                                    'course.assignments',
                                                                    {
                                                                        count: topic
                                                                            .assignments
                                                                            .length,
                                                                    }
                                                                )}
                                                            </>
                                                        )}
                                                        {topic.evaluations
                                                            .length > 0 && (
                                                            <>
                                                                {' '}
                                                                •{' '}
                                                                {
                                                                    topic
                                                                        .evaluations
                                                                        .length
                                                                }{' '}
                                                                {t(
                                                                    'course.evaluations',
                                                                    {
                                                                        count: topic
                                                                            .evaluations
                                                                            .length,
                                                                    }
                                                                )}
                                                            </>
                                                        )}
                                                    </p>
                                                    {isAssignmentOnlyTopic && (
                                                        <p className="mt-1 text-xs font-semibold tracking-wide text-amber-700 uppercase">
                                                            {t(
                                                                'course.assignment'
                                                            )}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={clsx(
                                                        'rounded-full px-2.5 py-1 text-xs font-semibold',
                                                        rightPillClass
                                                    )}
                                                >
                                                    {isExpanded
                                                        ? t('course.closeTopic')
                                                        : t('course.openTopic')}
                                                </span>
                                                <span
                                                    className={clsx(
                                                        'inline-flex h-7 w-7 items-center justify-center rounded-full border',
                                                        rightIconClass
                                                    )}
                                                >
                                                    {isExpanded ? (
                                                        <ChevronDown className="h-4 w-4" />
                                                    ) : (
                                                        <ChevronRight className="h-4 w-4" />
                                                    )}
                                                </span>
                                            </div>
                                        </button>

                                        {isExpanded && (
                                            <>
                                                {/* Topic Classes Grid */}
                                                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                                    {topic.classes.map(
                                                        (classData, index) => (
                                                            <ClassCard
                                                                key={
                                                                    classData.id
                                                                }
                                                                classData={
                                                                    classData
                                                                }
                                                                classNumber={
                                                                    index + 1
                                                                }
                                                                onClick={() =>
                                                                    handleClassClick(
                                                                        classData
                                                                    )
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </div>

                                                {topic.assignments.length >
                                                    0 && (
                                                    <GlassCard className="p-3">
                                                        <p className="mb-2 px-1 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-neutral-400">
                                                            {t(
                                                                'course.assignments',
                                                                {
                                                                    count: topic
                                                                        .assignments
                                                                        .length,
                                                                }
                                                            )}
                                                        </p>
                                                        <div className="space-y-1">
                                                            {topic.assignments.map(
                                                                (
                                                                    assignment
                                                                ) => (
                                                                    <AssignmentListItem
                                                                        key={`section-topic-assignment-${topic.id}-${assignment.id}`}
                                                                        assignment={
                                                                            assignment
                                                                        }
                                                                    />
                                                                )
                                                            )}
                                                        </div>
                                                    </GlassCard>
                                                )}

                                                {topic.evaluations.length >
                                                    0 && (
                                                    <GlassCard className="p-3">
                                                        <p className="mb-2 px-1 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-neutral-400">
                                                            {t(
                                                                'course.evaluations',
                                                                {
                                                                    count: topic
                                                                        .evaluations
                                                                        .length,
                                                                }
                                                            )}
                                                        </p>
                                                        <div className="space-y-1">
                                                            {topic.evaluations.map(
                                                                (
                                                                    evaluation
                                                                ) => (
                                                                    <EvaluationListItem
                                                                        key={`section-topic-evaluation-${topic.id}-${evaluation.id}`}
                                                                        evaluation={
                                                                            evaluation
                                                                        }
                                                                    />
                                                                )
                                                            )}
                                                        </div>
                                                    </GlassCard>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })
                        )}

                        {sectionEvaluations.length > 0 && (
                            <div className="space-y-4">
                                <h2 className="text-xl font-bold text-gray-900 capitalize dark:text-white">
                                    {t('course.evaluations', {
                                        count: sectionEvaluations.length,
                                    })}
                                </h2>
                                <GlassCard className="p-3">
                                    <div className="space-y-1">
                                        {sectionEvaluations.map(
                                            (evaluation) => (
                                                <EvaluationListItem
                                                    key={`section-evaluation-${evaluation.id}`}
                                                    evaluation={evaluation}
                                                />
                                            )
                                        )}
                                    </div>
                                </GlassCard>
                            </div>
                        )}

                        {sectionAssignments.length > 0 && (
                            <div className="space-y-4">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {t('course.assignmentsTitle')}
                                </h2>
                                <GlassCard className="p-3">
                                    <div className="space-y-1">
                                        {sectionAssignments.map(
                                            (assignment) => (
                                                <AssignmentListItem
                                                    key={`section-assignment-${assignment.id}`}
                                                    assignment={assignment}
                                                />
                                            )
                                        )}
                                    </div>
                                </GlassCard>
                            </div>
                        )}

                        {amaSeries.length > 0 && (
                            <div className="space-y-4">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    AMAs
                                </h2>
                                <GlassCard className="p-3">
                                    <div className="space-y-1">
                                        {amaSeries.map((series) => (
                                            <AmaSeriesListItem
                                                key={`section-ama-series-${series.id}`}
                                                amaSeries={series}
                                                courseSlug={course.slug}
                                            />
                                        ))}
                                    </div>
                                </GlassCard>
                            </div>
                        )}
                    </div>

                    {/* Navigation Footer */}
                    {(() => {
                        const accessibleClasses = section.classes.filter(
                            (c) => !c.coming_soon
                        );
                        const hasCompletedClasses = accessibleClasses.some(
                            (c) => c.is_completed
                        );
                        const firstIncompleteClass = accessibleClasses.find(
                            (c) => !c.is_completed
                        );
                        const targetClass = hasCompletedClasses
                            ? (firstIncompleteClass ?? accessibleClasses[0])
                            : accessibleClasses[0];
                        return targetClass ? (
                            <GlassCard className="mt-12 p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                                            {hasCompletedClasses
                                                ? t(
                                                      'course.continueWhereYouLeftOff'
                                                  )
                                                : t('course.readyToStart')}
                                        </h3>
                                        <p className="text-gray-600 dark:text-neutral-400">
                                            {hasCompletedClasses
                                                ? t('course.continueNextClass')
                                                : t(
                                                      'course.startFirstClassSection'
                                                  )}
                                        </p>
                                    </div>

                                    <button
                                        onClick={() =>
                                            handleClassClick(targetClass)
                                        }
                                        className="btn-gradient flex items-center space-x-2 rounded-lg px-6 py-3 font-medium"
                                    >
                                        <Play size={18} fill="currentColor" />
                                        <span>
                                            {hasCompletedClasses
                                                ? t('common.continue')
                                                : t('course.start')}
                                        </span>
                                    </button>
                                </div>
                            </GlassCard>
                        ) : null;
                    })()}
                </div>
            </div>
        </AppLayout>
    );
};

const SectionDetail: React.FC<PageProps> = ({ course, section }) => {
    return (
        <ThemeWrapper>
            <SectionDetailContent />
        </ThemeWrapper>
    );
};

export default SectionDetail;
